<template>
  <div class="d-flex justify-center align-start time-entry">
    <div>
      <v-text-field
        :class="{ 'no-timezone': hideTimezone }"
        density="compact"
        v-bind="$props"
        class="flex-grow-1 text-center input mt-0"
        inputmode="numeric"
        :data-testid="`${$attrs['test-id']}-input`"
        :placeholder="militaryTimeEnabled ? '13:00' : '12:00'"
        :model-value="time"
        @update:model-value="onTimeInput"
        variant="outlined"
        hide-details="auto"
        :rules="timeRules"
        validate-on="blur"
        @blur="normalizeTime"></v-text-field>
      <small class="text-right font-size-x-small" v-if="!hideTimezone">
        {{ timezone }}
      </small>
    </div>
    <div class="ml-2" v-if="!militaryTimeEnabled">
      <v-btn-toggle
        v-model="amPm"
        mandatory
        color="primary"
        class="align-end"
        divided
        density="comfortable"
        :disabled="disabled">
        <v-btn
          height="40"
          variant="outlined"
          value="am"
          :data-testid="`${$attrs['test-id']}-am-button`"
          :class="{ 'px-5': !compact, 'auto-width': compact }">
          AM
        </v-btn>
        <v-btn
          height="40"
          variant="outlined"
          value="pm"
          :data-testid="`${$attrs['test-id']}-pm-button`"
          :class="{ 'px-5': !compact, 'auto-width': compact }">
          PM
        </v-btn>
      </v-btn-toggle>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { formatParsedClockTime, parseClockTimeInput } from '@nova/core';

export default {
  props: {
    modelValue: {
      type: Object,
      required: false,
      default() {
        return {
          amPm: '',
          time: ''
        };
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: true
    },
    fontSizeLarge: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    hideTimezone: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      time: '',
      amPm: ''
    };
  },
  computed: {
    timeRules() {
      if (!this.required && this.time.length === 0) {
        return [];
      }
      return this.militaryTimeEnabled ? this.$validator.rules.time24 : this.$validator.rules.time;
    }
  },
  methods: {
    onTimeInput(value) {
      const digitsAndColon = String(value ?? '').replace(/[^\d:]/gu, '');
      const colonIndex = digitsAndColon.indexOf(':');

      if (colonIndex === -1) {
        this.time = digitsAndColon.slice(0, 4);
        return;
      }

      const hour = digitsAndColon.slice(0, colonIndex).replace(/:/gu, '').slice(0, 2);
      const minute = digitsAndColon
        .slice(colonIndex + 1)
        .replace(/:/gu, '')
        .slice(0, 2);
      this.time = `${hour}:${minute}`;
    },
    normalizeTime() {
      const parsed = parseClockTimeInput(this.time, this.militaryTimeEnabled);
      if (parsed) {
        this.time = formatParsedClockTime(parsed);
      }
    },
    updateVal() {
      if (this.time.length === 5 && this.militaryTimeEnabled) {
        this.amPm = DateTime.fromFormat(this.time, 'HH:mm').toFormat('a');
      }

      this.$emit('update:model-value', {
        amPm: this.amPm,
        time: this.time
      });
    },
    updateLocalTime() {
      if (this.modelValue.time.length === 5 && this.militaryTimeEnabled) {
        this.time = DateTime.fromFormat(
          `${this.modelValue.time} ${this.modelValue.amPm}`,
          'hh:mm a'
        ).toFormat('HH:mm');
        this.amPm = DateTime.fromFormat(this.time, 'HH:mm').toFormat('a');
      } else {
        this.time = this.modelValue.time;
        this.amPm = this.modelValue.amPm;
      }
    }
  },
  beforeMount() {
    this.updateLocalTime();
  },
  watch: {
    modelValue: {
      handler() {
        this.updateLocalTime();
      },
      deep: true
    },
    amPm() {
      this.updateVal();
    },
    time() {
      this.updateVal();
    }
  }
};
</script>

<style scoped lang="scss">
.no-timezone {
  width: 75px;
}
</style>
